import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Music, RefreshCw, AlertCircle, CheckCircle, Save, 
  Database, ToggleLeft, ToggleRight, GripVertical, ChevronDown, ChevronRight, FileAudio 
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Admin Themes Management (Source of Truth)
 * 
 * V1 Final: All audio is automated via R2.
 * This screen manages theme metadata (names, order, active status).
 */
const AdminSounds = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Themes state
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // R2 Status state
  const [r2Status, setR2Status] = useState(null);
  const [r2Loading, setR2Loading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [voiceProfiles, setVoiceProfiles] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const ADMIN_BASE = (API_URL || 'https://hearen-backend.onrender.com').replace(/\/$/, '');
  // Get real R2 track count (source of truth)
  const getR2Count = (key) => {
    return r2Status?.categories?.find(cat => cat.key === key)?.file_count;
  };
  
  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  // Load themes on mount
  useEffect(() => {
    loadThemes();
    loadR2Status();
  }, []);

  const loadThemes = async () => {
    setLoading(true);
    try {
      console.log('[AdminSounds] Fetch URL:', `${ADMIN_BASE}/api/admin/themes`);
      const response = await fetch(`${ADMIN_BASE}/api/admin/themes`);
      if (response.ok) {
        const data = await response.json();
        setThemes(data.themes || []);
        if (data.voice_profiles) setVoiceProfiles(data.voice_profiles);
      }
    } catch (error) {
      console.error('Error loading themes:', error);
      setErrorMessage('Erreur lors du chargement des thèmes');
    } finally {
      setLoading(false);
    }
  };

  const loadR2Status = async () => {
    setR2Loading(true);
    try {
      console.log('[AdminSounds] Fetch URL:', `${ADMIN_BASE}/api/admin/r2-status`);
      const response = await fetch(`${ADMIN_BASE}/api/admin/r2-status`);
      if (response.ok) {
        const data = await response.json();
        setR2Status(data);
      }
    } catch (error) {
      console.error('Error loading R2 status:', error);
    } finally {
      setR2Loading(false);
    }
  };

  const handleRescanR2 = async () => {
    setR2Loading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/r2-rescan`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'Rescan terminé');
        setTimeout(() => setSuccessMessage(''), 3000);
        await loadR2Status();
        await loadThemes(); // Refresh track counts
      }
    } catch (error) {
      console.error('Error rescanning R2:', error);
      setErrorMessage('Erreur lors du rescan R2');
    } finally {
      setR2Loading(false);
    }
  };

  const handleToggleActive = (index) => {
    const newThemes = [...themes];
    newThemes[index] = { ...newThemes[index], active: !newThemes[index].active };
    setThemes(newThemes);
    setHasChanges(true);
  };

  const handleSaveThemes = async () => {
    setSaving(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${ADMIN_BASE}/api/admin/themes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          themes: themes,
          user_email: user?.email
        })
      });
      
      if (response.ok) {
        setSuccessMessage('Thèmes sauvegardés avec succès');
        setHasChanges(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        setErrorMessage(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving themes:', error);
      setErrorMessage('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const moveTheme = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === themes.length - 1)
    ) return;
    
    const newThemes = [...themes];
    const temp = newThemes[index];
    newThemes[index] = newThemes[index + direction];
    newThemes[index + direction] = temp;
    
    // Update order values
    newThemes.forEach((theme, idx) => {
      theme.order = idx + 1;
    });
    
    setThemes(newThemes);
    setHasChanges(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 pb-40">
      <div className="max-w-6xl mx-auto">
        <PageHeader t={t} />

        {/* Main Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2" style={{ color: '#e2e8f0' }}>
                  <Music className="h-6 w-6" />
                  Gestion des Thèmes (Source de Vérité)
                </CardTitle>
                <CardDescription style={{ color: '#94a3b8' }}>
                  Définissez les noms, l'ordre et le statut des thèmes audio
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Info Alert */}
            <Alert style={{ backgroundColor: '#1e293b', borderColor: '#334155' }}>
              <CheckCircle className="h-4 w-4 text-green-400" />
              <AlertDescription style={{ color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Bibliothèque 100% automatisée</strong>
                <br />
                Tous les fichiers audio (.m4a) sont hébergés sur Cloudflare R2 et détectés automatiquement.
                <br />
                Cette interface permet uniquement de gérer les métadonnées des thèmes (noms, ordre, statut actif).
              </AlertDescription>
            </Alert>

            {/* Messages */}
            {errorMessage && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            
            {successMessage && (
              <Alert className="bg-green-50 border-green-300">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
              </Alert>
            )}

            {/* Themes Table */}
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: '#334155' }}>
              <div className="px-4 py-3 border-b" style={{ backgroundColor: '#1e293b', borderColor: '#334155' }}>
                <h3 className="font-semibold flex items-center gap-2" style={{ color: '#e2e8f0' }}>
                  <Database className="h-5 w-5" />
                  Thèmes (Source de Vérité)
                </h3>
              </div>
              
              {loading ? (
                <div className="p-8 text-center" style={{ color: '#94a3b8' }}>
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Chargement des thèmes...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b" style={{ backgroundColor: '#0f172a', borderColor: '#334155' }}>
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase w-16" style={{ color: '#94a3b8' }}>Ordre</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#94a3b8' }}>Thème (Clé R2)</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold uppercase w-20" style={{ color: '#94a3b8' }}>Tracks</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold uppercase w-36" style={{ color: '#94a3b8' }}>Profil vocal</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold uppercase w-20" style={{ color: '#94a3b8' }}>Actif</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold uppercase w-24" style={{ color: '#94a3b8' }}>Déplacer</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ divideColor: '#334155' }}>
                      {themes.map((theme, index) => (
                        <tr key={theme.key} className={`${!theme.active ? 'opacity-50' : ''}`} style={{ backgroundColor: index % 2 === 0 ? '#1e293b' : '#162032' }}>
                          <td className="px-3 py-3 text-sm text-center" style={{ color: '#94a3b8' }}>
                            {theme.order || index + 1}
                          </td>
                          <td className="px-3 py-3">
                            <code className="text-sm px-3 py-1.5 rounded font-mono font-semibold" style={{ backgroundColor: '#7c3aed', color: '#f5f3ff' }}>
                              {theme.key}
                            </code>
                            <span className="ml-2 text-xs" style={{ color: '#64748b' }}>
                              (source: R2 bucket)
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full ${
                              (getR2Count(theme.key) ?? theme.track_count ?? 0) > 0
                                ? 'bg-green-900/40 text-green-400' 
                                : 'text-gray-500'
                            }`} style={(getR2Count(theme.key) ?? theme.track_count ?? 0) > 0 ? {} : { backgroundColor: '#334155' }}>
                              {getR2Count(theme.key) ?? theme.track_count ?? 0}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <select
                              value={theme.voice_profile || 'balanced'}
                              onChange={(e) => {
                                const updated = [...themes];
                                updated[index] = { ...updated[index], voice_profile: e.target.value };
                                setThemes(updated);
                                setHasChanges(true);
                              }}
                              style={{ color: '#e2e8f0', backgroundColor: '#0f172a', borderColor: '#475569' }}
                              className="text-xs border rounded px-2 py-1.5 focus:ring-1 focus:ring-purple-400"
                              data-testid={`voice-profile-${theme.key}`}
                            >
                              <option value="very_talkative">Bavard (90s)</option>
                              <option value="balanced">Equilibre (180s)</option>
                              <option value="quiet">Discret (300s)</option>
                            </select>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={() => handleToggleActive(index)}
                              className={`p-1 rounded transition-colors ${
                                theme.active 
                                  ? 'text-green-400 hover:bg-green-900/30' 
                                  : 'text-gray-500 hover:bg-gray-700'
                              }`}
                              title={theme.active ? 'Actif - Cliquer pour désactiver' : 'Inactif - Cliquer pour activer'}
                            >
                              {theme.active ? (
                                <ToggleRight className="h-6 w-6" />
                              ) : (
                                <ToggleLeft className="h-6 w-6" />
                              )}
                            </button>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => moveTheme(index, -1)}
                                disabled={index === 0}
                                className="p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                                style={{ color: '#94a3b8' }}
                                title="Monter"
                              >
                                ↑
                              </button>
                              <GripVertical className="h-4 w-4" style={{ color: '#475569' }} />
                              <button
                                onClick={() => moveTheme(index, 1)}
                                disabled={index === themes.length - 1}
                                className="p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                                style={{ color: '#94a3b8' }}
                                title="Descendre"
                              >
                                ↓
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Save Button */}
              <div className="px-4 py-3 border-t flex justify-between items-center" style={{ backgroundColor: '#0f172a', borderColor: '#334155' }}>
                <span className="text-sm" style={{ color: hasChanges ? '#fbbf24' : '#64748b' }}>
                  {hasChanges ? 'Modifications non sauvegardées' : 'À jour'}
                </span>
                <Button
                  onClick={handleSaveThemes}
                  disabled={!hasChanges || saving}
                  className={`${hasChanges ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400'}`}
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Sauvegarder
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* R2 Status Section */}
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: '#334155', backgroundColor: '#0f172a' }}>
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ backgroundColor: '#1e293b', borderColor: '#334155' }}>
                <h3 className="font-semibold flex items-center gap-2" style={{ color: '#e2e8f0' }}>
                  <Database className="h-5 w-5" />
                  Statut Bibliothèque R2 (Lecture seule)
                </h3>
                <Button
                  onClick={handleRescanR2}
                  disabled={r2Loading}
                  variant="outline"
                  size="sm"
                  className="border-blue-400 text-blue-300 hover:bg-blue-900/30"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${r2Loading ? 'animate-spin' : ''}`} />
                  Rescanner
                </Button>
              </div>
              
              {r2Status ? (
                <div className="p-4 space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="rounded-lg p-3" style={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}>
                      <p className="text-xs uppercase" style={{ color: '#94a3b8' }}>Total fichiers</p>
                      <p className="text-2xl font-bold text-purple-400">{r2Status.total_files}</p>
                    </div>
                    <div className="rounded-lg p-3" style={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}>
                      <p className="text-xs uppercase" style={{ color: '#94a3b8' }}>Catégories</p>
                      <p className="text-2xl font-bold text-blue-400">{r2Status.categories?.length || 0}</p>
                    </div>
                    <div className="rounded-lg p-3" style={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}>
                      <p className="text-xs uppercase" style={{ color: '#94a3b8' }}>Cache TTL</p>
                      <p className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>{Math.round((r2Status.cache?.ttl_seconds || 0) / 60)}min</p>
                    </div>
                    <div className="rounded-lg p-3" style={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}>
                      <p className="text-xs uppercase" style={{ color: '#94a3b8' }}>Age cache</p>
                      <p className="text-2xl font-bold text-green-400">
                        {r2Status.cache?.age_seconds != null 
                          ? `${Math.round(r2Status.cache.age_seconds / 60)}min`
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Per-category breakdown with expandable file list */}
                  <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #334155' }}>
                    <table className="w-full text-sm">
                      <thead style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155' }}>
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold w-8" style={{ color: '#94a3b8' }}></th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: '#94a3b8' }}>Catégorie</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold" style={{ color: '#94a3b8' }}>Fichiers</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: '#94a3b8' }}>Premier</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: '#94a3b8' }}>Dernier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r2Status.categories?.map((cat, index) => (
                          <React.Fragment key={cat.key}>
                            <tr 
                              className="cursor-pointer hover:brightness-110"
                              onClick={() => toggleCategory(cat.key)}
                              style={{ backgroundColor: index % 2 === 0 ? '#1e293b' : '#162032', borderBottom: '1px solid #334155' }}
                            >
                              <td className="px-3 py-2">
                                {expandedCategories[cat.key] ? (
                                  <ChevronDown className="h-4 w-4" style={{ color: '#94a3b8' }} />
                                ) : (
                                  <ChevronRight className="h-4 w-4" style={{ color: '#94a3b8' }} />
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <code className="text-sm px-3 py-1 rounded font-mono font-semibold" style={{ backgroundColor: '#7c3aed', color: '#f5f3ff' }}>
                                  {cat.key}
                                </code>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full bg-green-900/40 text-green-400">
                                  {cat.file_count}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs font-mono" style={{ color: '#94a3b8' }}>{cat.first_file || '-'}</td>
                              <td className="px-3 py-2 text-xs font-mono" style={{ color: '#94a3b8' }}>{cat.last_file || '-'}</td>
                            </tr>
                            {/* Expanded file list */}
                            {expandedCategories[cat.key] && cat.files && (
                              <tr>
                                <td colSpan="5" className="px-4 py-3" style={{ backgroundColor: '#0f172a' }}>
                                  <div className="max-h-64 overflow-y-auto">
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                      {cat.files.map((file, idx) => (
                                        <div 
                                          key={idx}
                                          className="flex items-center gap-2 px-2 py-1.5 rounded text-xs font-mono transition-colors hover:brightness-125"
                                          style={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#cbd5e1' }}
                                        >
                                          <FileAudio className="h-3 w-3 text-purple-400 flex-shrink-0" />
                                          <span className="truncate">{file}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center" style={{ color: '#94a3b8' }}>
                  {r2Loading ? (
                    <>
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Chargement du statut R2...
                    </>
                  ) : (
                    'Cliquez sur "Rescanner" pour charger le statut'
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSounds;
